/**
 * VignettePreview — the printable tube label (R-10). The server returns the structured payload
 * (`{ qr: { v, t }, human: {...} }`); this renders the human strip + the QR payload, and the print
 * CSS isolates `.smp-print-area` for window.print().
 *
 * NOTE: a true QR RASTER needs an encoder (the server doc names `qrcode`). That dependency is NOT in
 * the scaffold, so until it is added we render the opaque token as a scannable mono code block
 * (a 1D/2D scanner reads the printed payload, and the strip is always human-legible). Swapping in a
 * <QRCode value={payload} /> here is a one-line change once `qrcode`/`qrcode.react` is installed.
 */
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { Vignette } from "../types";

export function VignettePreview({ vignette }: { vignette: Vignette }) {
  const { t } = useT();
  const { human, qr } = vignette;
  // The exact string a scanner resolves back to the tube (mirror of server qr payload `{v,t}`).
  const payload = JSON.stringify(qr);

  return (
    <div className="smp-vignette smp-print-area">
      <div className="smp-vignette__qr nv-ltr-island" aria-hidden>
        {qr.t ? (
          <pre
            className="nv-mono"
            style={{
              margin: 0,
              padding: token.s2,
              border: `1px solid ${token.line}`,
              borderRadius: token.rXs,
              fontSize: token.fsCaption,
              wordBreak: "break-all",
              whiteSpace: "pre-wrap",
              maxInlineSize: "50mm",
            }}
          >
            {payload}
          </pre>
        ) : (
          <span style={{ color: token.steel, fontSize: token.fsCaption }}>
            {t("sampling.print.noToken")}
          </span>
        )}
      </div>

      <div className="smp-vignette__strip nv-ltr-island">
        {human.matricule && (
          <div className="smp-vignette__matricule">{human.matricule}</div>
        )}
        {(human.nom || human.prenom) && (
          <div>
            {human.nom ?? ""} {human.prenom ?? ""}
          </div>
        )}
        {human.demande_ref && <div>{human.demande_ref}</div>}
      </div>
    </div>
  );
}
